import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  modelRegistryApi,
  registryErrorMessage,
} from "@/services/modelRegistryApi";
import type {
  AdminModel,
  ModelProvider,
  ModelRoleBinding,
} from "@/types/modelRegistry";

export interface ModelRegistryState {
  models: AdminModel[] | null;
  providers: ModelProvider[] | null;
  roles: ModelRoleBinding[] | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

/** One load for the whole page; every write calls `refresh`. The registry is small. */
export function useModelRegistry(): ModelRegistryState {
  const [models, setModels] = useState<AdminModel[] | null>(null);
  const [providers, setProviders] = useState<ModelProvider[] | null>(null);
  const [roles, setRoles] = useState<ModelRoleBinding[] | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [nextModels, nextProviders, nextRoles] = await Promise.all([
        modelRegistryApi.listModels(),
        modelRegistryApi.listProviders(),
        modelRegistryApi.listRoles(),
      ]);
      setModels(nextModels);
      setProviders(nextProviders);
      setRoles(nextRoles);
    } catch (error) {
      toast.error(
        `Could not load the model registry: ${registryErrorMessage(error)}`,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { models, providers, roles, loading, refresh };
}
